import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

// Retargeting pixels for anonymous (not-yet-signed-up) visitors.
//
// Strategy (see /app/memory/ADVERTISING_BRIEF.md):
//  - Fire ad-platform pixels ONLY for anonymous visitors, so every person
//    exposed to Tagdada joins the retargeting audience.
//  - Once the visitor signs up (auth `user` present) we STOP loading pixels,
//    so converted users drop out of the "exposed but not signed up" audience.
//  - Gated behind cookie consent (152-ФЗ / GDPR) — nothing fires until the
//    visitor accepts cookies.
//  - Inert until the corresponding REACT_APP_* pixel id is configured at
//    deploy, so no network calls happen in preview / without ids.
const PIXELS = {
  meta: process.env.REACT_APP_META_PIXEL_ID,
  ga: process.env.REACT_APP_GA_MEASUREMENT_ID,
  yandexMetrica: process.env.REACT_APP_YANDEX_METRICA_ID,
  vk: process.env.REACT_APP_VK_PIXEL_ID,
};

function injectOnce(id, fn) {
  if (document.getElementById(id)) return;
  const marker = document.createElement('meta');
  marker.id = id;
  document.head.appendChild(marker);
  fn();
}

export default function RetargetingPixels() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return;
    if (user) return; // converted → stop retargeting
    let consent = null;
    try { consent = localStorage.getItem('cookie_consent'); } catch (_) {}
    if (consent !== 'accepted') return;

    if (PIXELS.meta) {
      injectOnce('px-meta', () => {
        /* eslint-disable */
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
        n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
        document,'script','https://connect.facebook.net/en_US/fbevents.js');
        window.fbq('init', PIXELS.meta);
        window.fbq('track', 'PageView');
        /* eslint-enable */
      });
    }

    if (PIXELS.ga) {
      injectOnce('px-ga', () => {
        const s = document.createElement('script');
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${PIXELS.ga}`;
        document.head.appendChild(s);
        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', PIXELS.ga);
      });
    }

    if (PIXELS.yandexMetrica) {
      injectOnce('px-ym', () => {
        /* eslint-disable */
        (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
        m[i].l=1*new Date();k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,
        k.src=r,a.parentNode.insertBefore(k,a)})(window,document,'script',
        'https://mc.yandex.ru/metrika/tag.js','ym');
        window.ym(PIXELS.yandexMetrica, 'init', { clickmap:true, trackLinks:true, accurateTrackBounce:true });
        /* eslint-enable */
      });
    }

    if (PIXELS.vk) {
      injectOnce('px-vk', () => {
        /* eslint-disable */
        var t=document.createElement('script');t.type='text/javascript';t.async=true;
        t.src='https://vk.com/js/api/openapi.js?169';
        t.onload=function(){window.VK&&window.VK.Retargeting&&(window.VK.Retargeting.Init(PIXELS.vk),window.VK.Retargeting.Hit());};
        document.head.appendChild(t);
        /* eslint-enable */
      });
    }
  }, [user, loading]);

  return null;
}
